import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { FairUseMonthLock } from "@/api/admin/fair-use"

import { Download, Lock, Plus } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { formatMonthLabel } from "./fair-use-ops-utils"

type FairUseMonthBarProps = {
  month: string
  monthOptions: readonly string[]
  onMonthChange: (month: string) => void
  /**
   * The billing lock, or `null` when the viewer is not a platform admin.
   *
   * `null` means "you may not see this", not "open": the whole lock/export
   * group is hidden, rather than rendered disabled, so a customer admin is
   * never shown an affordance whose every call would 403.
   */
  lock: FairUseMonthLock | null
  isLocking: boolean
  isExporting: boolean
  /**
   * True when `month` is the still-accumulating current UTC month. Lock is
   * refused with 409 `month_not_locked` for that month, so the button is
   * disabled rather than offering a confirm that can only fail.
   */
  isCurrentMonth: boolean
  onLock: () => void
  onExport: () => void
  onTopUp: () => void
}

export function FairUseMonthBar({
  month,
  monthOptions,
  onMonthChange,
  lock,
  isLocking,
  isExporting,
  isCurrentMonth,
  onLock,
  onExport,
  onTopUp,
}: FairUseMonthBarProps) {
  const { t, i18n } = useTranslation()
  const [confirmKind, setConfirmKind] = useState<"lock" | "export" | null>(null)
  const monthLabel = formatMonthLabel(month, i18n.language)
  const lockDisabled = Boolean(lock?.locked) || isLocking || isCurrentMonth
  // GET .../export.csv lazy-locks any PAST open month. Confirm that freeze
  // the same way Lock does. An already-locked past month is an idempotent
  // read; the current month exports live and stays open.
  const exportWouldLock = Boolean(lock && !lock.locked && !isCurrentMonth)

  return (
    <div className="flex w-full flex-wrap items-center justify-between gap-3">
      <div className="flex shrink-0 items-center gap-2">
        <Select value={month} onValueChange={onMonthChange}>
          <SelectTrigger
            aria-label={t("adminDashboard.fairUse.ops.monthLabel", "Month")}
            className="h-10 w-[170px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {formatMonthLabel(option, i18n.language)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {lock && (
          <Badge className="h-6" variant={lock.locked ? "disabled" : "success"}>
            {lock.locked
              ? t("adminDashboard.fairUse.ops.monthLocked", "Month locked")
              : t("adminDashboard.fairUse.ops.monthOpen", "Month open")}
          </Badge>
        )}
      </div>
      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
        {lock && (
          <>
            <Button
              className="h-10"
              disabled={isExporting}
              variant="outline"
              onClick={() => {
                if (exportWouldLock) setConfirmKind("export")
                else onExport()
              }}
            >
              <Download className="mr-2 size-4" />
              {t("adminDashboard.fairUse.ops.exportBilling", "Export billing basis")}
            </Button>
            <Button
              className="h-10"
              disabled={lockDisabled}
              variant="outline"
              title={
                isCurrentMonth
                  ? t(
                      "adminDashboard.fairUse.ops.lockCurrentMonthHint",
                      "The current month cannot be locked while it is still accumulating"
                    )
                  : undefined
              }
              onClick={() => setConfirmKind("lock")}
            >
              <Lock className="mr-2 size-4" />
              {t("adminDashboard.fairUse.ops.lockMonth", "Lock month")}
            </Button>
          </>
        )}

        <Button className="h-10" onClick={onTopUp}>
          <Plus className="mr-2 size-4" />
          {t("adminDashboard.fairUse.ops.topUp", "Top up a user")}
        </Button>
      </div>

      <AlertDialog
        open={confirmKind !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmKind(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmKind === "export"
                ? t(
                    "adminDashboard.fairUse.ops.exportLockConfirmTitle",
                    "Exporting will lock {{month}}?",
                    { month: monthLabel }
                  )
                : t("adminDashboard.fairUse.ops.lockConfirmTitle", "Lock {{month}}?", {
                    month: monthLabel,
                  })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmKind === "export"
                ? t(
                    "adminDashboard.fairUse.ops.exportLockConfirmBody",
                    "Downloading the billing basis for {{month}} will lock it. After this, no top-up, approval or withdrawal can change what {{month}} invoices. This cannot be undone.",
                    { month: monthLabel }
                  )
                : t(
                    "adminDashboard.fairUse.ops.lockConfirmBody",
                    "After this, no top-up, approval or withdrawal can change what {{month}} invoices. This cannot be undone.",
                    { month: monthLabel }
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", "Cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const kind = confirmKind
                setConfirmKind(null)
                if (kind === "export") onExport()
                else if (kind === "lock") onLock()
              }}
            >
              {confirmKind === "export"
                ? t("adminDashboard.fairUse.ops.exportBilling", "Export billing basis")
                : t("adminDashboard.fairUse.ops.lockMonth", "Lock month")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
